import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, RefreshCw, ArrowLeft, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';

export default function EmailVerificationPendingPage() {
  const location = useLocation();
  const email = location.state?.email || '';
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');

  const handleResendEmail = async () => {
    if (!email) {
      setResendError('이메일 정보가 없습니다. 다시 회원가입해주세요.');
      return;
    }

    setIsResending(true);
    setResendError('');
    setResendSuccess(false);
    setResendMessage('');

    try {
      const response = await fetch('/api/v1/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setResendSuccess(true);
        setResendMessage(data.message || '인증 이메일이 재발송되었습니다.');
      } else {
        setResendError(data.error || '인증 이메일 재발송에 실패했습니다.');
      }
    } catch (err) {
      setResendError('인증 이메일 재발송에 실패했습니다.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            {/* Icon */}
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10 text-primary" />
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              이메일을 확인해주세요
            </h1>

            {/* Description */}
            <p className="text-gray-600 mb-2">
              회원가입을 완료하려면 이메일 인증이 필요합니다.
            </p>
            {email && (
              <p className="text-primary font-medium mb-6">
                {email}
              </p>
            )}

            {/* Instructions */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">1.</span> 입력하신 이메일의 받은편지함을 확인해주세요.
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">2.</span> 스탬핏에서 보낸 인증 이메일을 찾아주세요.
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">3.</span> "이메일 인증하기" 버튼을 클릭해주세요.
              </p>
            </div>

            {/* Resend Success */}
            {resendSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-700">
                  {resendMessage}
                </p>
              </div>
            )}

            {/* Resend Error */}
            {resendError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">{resendError}</p>
              </div>
            )}

            {/* Resend Button */}
            <button
              onClick={handleResendEmail}
              disabled={isResending || !email}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4"
            >
              <RefreshCw className={`w-5 h-5 ${isResending ? 'animate-spin' : ''}`} />
              {isResending ? '발송 중...' : '인증 이메일 재발송'}
            </button>

            {/* Note */}
            <p className="text-xs text-gray-500 mb-6">
              이메일이 오지 않았나요? 스팸함을 확인해주세요.
            </p>

            {/* Back to Login */}
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>로그인 페이지로 돌아가기</span>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
